function isFresh(result: {until?: number}) {
  return !result.until || Date.now() <= result.until;
}

interface MkFetcherOptions<Args, Result, Key = string> {
  // async function that returns a `Result` based on the given `Args`
  fetcher: (args: Args) => Promise<Result>;
  // generates a unique string (hash) from the given `Args`
  hasher: (args: Args) => Key;
  // [optional] determine wether to store the `Result`
  // or not (in case it is an error)
  predicate?: (result: Result) => boolean;

  // [optional] Lifetime of result in cache (in milliseconds)
  lifetime?: (result: Result) => number | undefined;
}

export type Fetcher<Args, Result> = (args: Args) => Promise<Result>;

// TODO: Add regular job that invalidates stale cache items?

export const mkFetcher = <Args, Result, Key = string>({
  fetcher,
  hasher,
  predicate,
  lifetime,
}: MkFetcherOptions<Args, Result, Key>): Fetcher<Args, Result> => {
  // the In-Memory-Store instance
  const inMemoryStore: Map<Key, {result: Result, until?: number}> = new Map();

  const inProgressStore: Map<Key, Promise<Result>> = new Map();

  // update the `Result` based on the given `Args` and `Key` (the hash)
  const update = async (fetchParams: Args, hash: Key): Promise<Result> => {
    const pendingPromise = inProgressStore.get(hash);
    if (pendingPromise) {
      return pendingPromise;
    }

    const newPendingPromise = (async () => {
      const fetchResult = await fetcher(fetchParams);
      if (!predicate || predicate(fetchResult)) {
        const computedLifetime = lifetime ? lifetime(fetchResult) : undefined;
        const storeEntry = {
          result: fetchResult,
          until: computedLifetime ? Date.now() + computedLifetime : undefined,
        };
        inMemoryStore.set(hash, storeEntry);
      }

      return fetchResult;
    })();
    inProgressStore.set(hash, newPendingPromise);

    try {
      return await newPendingPromise;
    } catch (e) {
      throw e;
    } finally {
      inProgressStore.delete(hash);
    }
  };

  return async (fetchParams: Args) => {
    // generate the hash
    const hash = hasher(fetchParams);

    // load the `Result` from the In-Memory-Store
    const result = inMemoryStore.get(hash);
    if (result && isFresh(result)) {
      return result.result;
    }

    // if nothing holds, just update
    return await update(fetchParams, hash);
  };
};
